import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, Collapse, Typography, TextField, IconButton, Tooltip, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import NoTaskListComponent from "../Task/NoTaskListComponent";
import LoadingSpinner from "../../../common/LoadingSpinner/LoadingSpinner";
import TaskMessage from "../Task/TaskMessage";
import ApprovalMessage from "../Approval/ApprovalMessage";
import { MessageType } from "../../../utils/constant";
import { handleRenameThread } from "../../../appStore/actions/ArkchatGroup/action";

// Collect all IDs that appear as .chain in a set of items
const getChainIds = (items) => {
  const ids = new Set();
  items.forEach((item) => {
    let c = item.chain;
    while (c) {
      const cid = c.id || (c._id && c._id.toString());
      if (cid) ids.add(cid);
      c = c.chain;
    }
  });
  return ids;
};


/*
  Group types produced by buildThreadGroups:

  "thread_task"     – root item is a Task; its balah-type-Approval items are completion approvals
                      (shown connected below their task/subtask). Thread-child approvals are
                      independent (no connector). Both are shown.

  "thread_approval" – root item is an Approval (pure approval chain, no tasks). Shown as-is.

  "orphan"          – children whose thread parent is a plain chat message (not in the list).
                      All items are independent. Task items still show their balah completion
                      approvals via the connector; the approvals in the items list are independent.

  "standalone"      – single item with no thread children. Task items show their balah
                      completion approvals via the connector.
*/

// Extract completion approvals and enrich subtasks for a task item.
// Returns { enrichedItem, closingApprovals, totalApprovalCount }.
const enrichTaskItem = (item) => {
  // is_main: false  →  child item (completion approval or subtask)
  // is_main: true   →  independent item created in the thread (never connected)
  const closingApprovals = (item.balah || [])
    .filter((b) => b.type === MessageType.Approval && b.is_main === false)
    .sort((a, b) => a.timetoken - b.timetoken);

  const enrichedSubtasks = (item.balah || [])
    .filter((b) => b.type === MessageType.Task && b.is_main === false)
    .map((sub) => ({
      ...sub,
      closingApprovals: (sub.balah || [])
        .filter((b) => b.type === MessageType.Approval && b.is_main === false)
        .sort((a, b) => a.timetoken - b.timetoken),
      balah: (sub.balah || []).filter((b) => b.type === MessageType.Task && b.is_main === false),
    }));

  const totalApprovalCount =
    closingApprovals.length +
    enrichedSubtasks.reduce((sum, s) => sum + s.closingApprovals.length, 0);

  return {
    enrichedItem: { ...item, balah: enrichedSubtasks },
    closingApprovals,
    totalApprovalCount,
  };
};

const buildThreadGroups = (list) => {
  if (!list || list.length === 0) return [];

  const itemMap = new Map(list.map((i) => [i.id, i]));

  // Bucket children by thread_id
  const childrenByThread = {};
  list.forEach((item) => {
    if (item.thread_id) {
      if (!childrenByThread[item.thread_id]) childrenByThread[item.thread_id] = [];
      childrenByThread[item.thread_id].push(item);
    }
  });

  const groups = [];

  // Process root items (thread_id: null)
  list.forEach((item) => {
    if (item.thread_id) return;

    const children = childrenByThread[item.id] || [];

    if (children.length === 0) {
      // Standalone — no thread children
      if (item.type === MessageType.Task) {
        const { enrichedItem, closingApprovals, totalApprovalCount } = enrichTaskItem(item);
        groups.push({
          key: item.id,
          thread_number: item.thread_number,
          thread_heading: item.thread_heading,
          type: "standalone",
          item: enrichedItem,
          closingApprovals,
          totalApprovalCount,
        });
      } else {
        groups.push({ key: item.id, thread_number: item.thread_number, thread_heading: item.thread_heading, type: "standalone", item });
      }
      return;
    }

    const allItems = [item, ...children];
    const chainIds = getChainIds(allItems);

    if (item.type === MessageType.Task) {
      // --- thread_task ---
      // Use thread_original_message.id to identify completion approvals (the approval
      // was created specifically to close that task/subtask). Chain-embedded approvals
      // (chainIds) stay embedded in their parent approval card — they are never promoted
      // to standalone closing approvals even if thread_original_message points to a task.
      const completionApprovalsByTarget = {};
      children.forEach((child) => {
        if (child.type !== MessageType.Approval) return;
        if (chainIds.has(child.id)) return;
        const origId = child.thread_original_message?.id;
        if (!origId) return;
        const origItem = itemMap.get(origId);
        if (!origItem || origItem.type !== MessageType.Task) return;
        (completionApprovalsByTarget[origId] = completionApprovalsByTarget[origId] || []).push(child);
      });

      // Subtasks from balah (type:1, is_main:false), each annotated with their closing approvals
      const subtasks = (item.balah || [])
        .filter((b) => b.type === MessageType.Task && b.is_main === false)
        .map((sub) => ({
          ...sub,
          closingApprovals: (completionApprovalsByTarget[sub.id] || [])
            .sort((a, b) => a.timetoken - b.timetoken),
          balah: (sub.balah || []).filter((b) => b.type === MessageType.Task && b.is_main === false),
        }));

      const rootClosingApprovals = (completionApprovalsByTarget[item.id] || [])
        .sort((a, b) => a.timetoken - b.timetoken);

      const enrichedItem = { ...item, balah: subtasks };

      const allCompletionIds = new Set([
        ...rootClosingApprovals.map((a) => a.id),
        ...subtasks.flatMap((s) => s.closingApprovals.map((a) => a.id)),
      ]);

      const independentApprovals = children
        .filter(
          (c) =>
            c.type === MessageType.Approval &&
            !chainIds.has(c.id) &&
            !allCompletionIds.has(c.id)
        )
        .sort((a, b) => a.timetoken - b.timetoken);

      const totalApprovalCount =
        rootClosingApprovals.length +
        subtasks.reduce((sum, s) => sum + s.closingApprovals.length, 0) +
        independentApprovals.length;

      groups.push({
        key: item.id,
        thread_number: item.thread_number,
        thread_heading: item.thread_heading,
        type: "thread_task",
        rootItem: enrichedItem,
        rootClosingApprovals,
        independentApprovals,
        totalApprovalCount,
      });
    } else {
      // --- thread_approval ---
      const topApprovals = allItems
        .filter((i) => !chainIds.has(i.id))
        .sort((a, b) => a.timetoken - b.timetoken);

      groups.push({ key: item.id, thread_number: item.thread_number, thread_heading: item.thread_heading, type: "thread_approval", topApprovals });
    }
  });

  // Orphan threads — parent is a plain chat message, not in the list
  Object.entries(childrenByThread).forEach(([threadId, children]) => {
    if (!itemMap.has(threadId)) {
      const chainIds = getChainIds(children);
      const topItems = children
        .filter((c) => !chainIds.has(c.id))
        .sort((a, b) => a.timetoken - b.timetoken);

      // Enrich task items so their balah completion approvals are shown via connector
      const enrichedItems = topItems.map((i) => {
        if (i.type === MessageType.Task) {
          const { enrichedItem, closingApprovals } = enrichTaskItem(i);
          return { ...enrichedItem, closingApprovals };
        }
        return i;
      });

      groups.push({ key: threadId, type: "orphan", items: enrichedItems });
    }
  });

  return groups;
};

// Reusable thread header bar
const ThreadHeader = ({ threadId, threadNumber, threadHeading, taskCount, approvalCount, collapsed, onToggle }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [headingInput, setHeadingInput] = useState("");

  const label = threadNumber != null
    ? (threadHeading ? `# ${threadNumber} - ${threadHeading}` : `# Thread ${threadNumber}`)
    : null;

  const handleEditStart = (e) => {
    e.stopPropagation();
    setHeadingInput(threadHeading || "");
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (threadId) await dispatch(handleRenameThread(threadId, headingInput.trim()));
    setIsEditing(false);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setIsEditing(false);
  };

  return (
    <Box
      onClick={!isEditing ? onToggle : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "0.8rem",
        py: "0.35rem",
        background: "#F3F3F3",
        cursor: isEditing ? "default" : "pointer",
        borderBottom: collapsed ? "none" : "1px solid #E1E1E1",
        "&:hover": { background: "#E1E1E1" },
      }}
    >
      {isEditing ? (
        <Stack direction="row" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()} sx={{ flex: 1 }}>
          <TextField
            size="small"
            autoFocus
            value={headingInput}
            onChange={(e) => setHeadingInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave(e);
              if (e.key === "Escape") handleCancel(e);
            }}
            placeholder="Thread heading..."
            sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem", padding: "3px 6px" } }}
          />
          <Tooltip title="Save">
            <IconButton size="small" onClick={handleSave} sx={{ color: "#888" }}>
              <CheckIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton size="small" onClick={handleCancel} sx={{ color: "#888" }}>
              <CloseRoundedIcon sx={{ fontSize: "0.9rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontSize="12px"
            fontWeight={600}
            color="#183243"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {label ?? `Thread #${threadId}`}
          </Typography>
          <Tooltip title="Edit heading">
            <IconButton size="small" onClick={handleEditStart} sx={{ color: "#888", flexShrink: 0 }}>
              <EditIcon sx={{ fontSize: "0.85rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0, ml: "0.5rem" }}>
        {taskCount > 0 && (
          <Typography fontSize="11px" fontWeight={500} color="#777">
            {taskCount} Task
          </Typography>
        )}
        {taskCount > 0 && approvalCount > 0 && (
          <Typography fontSize="11px" color="#777">·</Typography>
        )}
        {approvalCount > 0 && (
          <Typography fontSize="11px" fontWeight={500} color="#777">
            {approvalCount} Approval
          </Typography>
        )}
        <Typography fontSize="11px" color="#555">
          {collapsed ? "▼" : "▲"}
        </Typography>
      </Stack>
    </Box>
  );
};

const INNER_PAD = { pt: "0.5rem", pl: "0.5rem" };

const ThreadGroupCard = ({
  group,
  threadParents,
  handleClickTaskUpdate,
  handleViewThreadClick,
  startTaskOpen,
  setStartTaskOpen,
  handleStartTask,
  recipientDetails,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = () => setCollapsed((p) => !p);

  // ── thread_task ──────────────────────────────────────────────
  if (group.type === "thread_task") {
    const { key, rootItem, rootClosingApprovals, independentApprovals, totalApprovalCount } = group;
    const subtaskCount = (rootItem.balah || []).length;
    const taskCount = 1 + subtaskCount;

    return (
      <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "4px", mb: "0.5rem", background: "#fff" }}>
        <ThreadHeader
          threadId={key}
          threadNumber={group.thread_number}
          threadHeading={group.thread_heading}
          taskCount={taskCount}
          approvalCount={totalApprovalCount}
          collapsed={collapsed}
          onToggle={toggle}
        />
        <Collapse in={!collapsed}>
          <Box sx={INNER_PAD}>
            <TaskMessage
              taskItem={rootItem}
              closingApprovals={rootClosingApprovals}
              startTaskOpen={startTaskOpen}
              setStartTaskOpen={setStartTaskOpen}
              handleStartTask={handleStartTask}
              indx={0}
              handleClickTaskUpdate={handleClickTaskUpdate}
              handleViewThreadClick={handleViewThreadClick}
              tabValue={1}
              recipientDetails={recipientDetails}
            />
            {independentApprovals.map((approval) => (
              <ApprovalMessage
                key={approval.id}
                taskItem={approval}
                indx={0}
                handleClickTaskUpdate={handleClickTaskUpdate}
                handleViewThreadClick={handleViewThreadClick}
                tabValue={2}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // ── thread_approval ──────────────────────────────────────────
  if (group.type === "thread_approval") {
    const { key, topApprovals } = group;

    return (
      <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "4px", mb: "0.5rem", background: "#fff" }}>
        <ThreadHeader
          threadId={key}
          threadNumber={group.thread_number}
          threadHeading={group.thread_heading}
          taskCount={0}
          approvalCount={topApprovals.length}
          collapsed={collapsed}
          onToggle={toggle}
        />
        <Collapse in={!collapsed}>
          <Box sx={INNER_PAD}>
            {topApprovals.map((approval) => (
              <ApprovalMessage
                key={approval.id}
                taskItem={approval}
                indx={0}
                handleClickTaskUpdate={handleClickTaskUpdate}
                handleViewThreadClick={handleViewThreadClick}
                tabValue={2}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // ── orphan (parent is a plain chat message) ──────────────────
  // All approvals in the items list are normal/independent — no connector between them.
  // Task items may still show balah completion approvals via the connector (handled by TaskMessage).
  if (group.type === "orphan") {
    const { key, items } = group;
    const taskCount = items.filter((i) => i.type === MessageType.Task).length;
    const approvalCount = items.filter((i) => i.type === MessageType.Approval).length;
    const orphanParent = threadParents?.[key] || {};

    return (
      <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "4px", mb: "0.5rem", background: "#fff" }}>
        <ThreadHeader
          threadId={key}
          threadNumber={orphanParent.thread_number}
          threadHeading={orphanParent.thread_heading}
          taskCount={taskCount}
          approvalCount={approvalCount}
          collapsed={collapsed}
          onToggle={toggle}
        />
        <Collapse in={!collapsed}>
          <Box sx={INNER_PAD}>
            {items.map((item, indx) =>
              item.type === MessageType.Task ? (
                <TaskMessage
                  key={item.id || indx}
                  taskItem={item}
                  closingApprovals={item.closingApprovals}
                  startTaskOpen={startTaskOpen}
                  setStartTaskOpen={setStartTaskOpen}
                  handleStartTask={handleStartTask}
                  indx={indx}
                  handleClickTaskUpdate={handleClickTaskUpdate}
                  handleViewThreadClick={handleViewThreadClick}
                  tabValue={1}
                  recipientDetails={recipientDetails}
                />
              ) : (
                <ApprovalMessage
                  key={item.id || indx}
                  taskItem={item}
                  indx={indx}
                  handleClickTaskUpdate={handleClickTaskUpdate}
                  handleViewThreadClick={handleViewThreadClick}
                  tabValue={2}
                />
              )
            )}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // ── standalone (single item, no thread children) ─────────────
  const { item, closingApprovals, totalApprovalCount } = group;
  const isTask = item.type === MessageType.Task;
  const subtaskCount = isTask ? (item.balah || []).length : 0;
  return (
    <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "4px", mb: "0.5rem", background: "#fff" }}>
      <ThreadHeader
        threadId={item.id}
        threadNumber={group.thread_number}
        threadHeading={group.thread_heading}
        taskCount={isTask ? 1 + subtaskCount : 0}
        approvalCount={isTask ? (totalApprovalCount || 0) : 1}
        collapsed={collapsed}
        onToggle={toggle}
      />
      <Collapse in={!collapsed}>
        <Box sx={INNER_PAD}>
          {isTask ? (
            <TaskMessage
              taskItem={item}
              closingApprovals={closingApprovals}
              startTaskOpen={startTaskOpen}
              setStartTaskOpen={setStartTaskOpen}
              handleStartTask={handleStartTask}
              indx={0}
              handleClickTaskUpdate={handleClickTaskUpdate}
              handleViewThreadClick={handleViewThreadClick}
              tabValue={1}
              recipientDetails={recipientDetails}
            />
          ) : (
            <ApprovalMessage
              taskItem={item}
              indx={0}
              handleClickTaskUpdate={handleClickTaskUpdate}
              handleViewThreadClick={handleViewThreadClick}
              tabValue={2}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const WorkTrackingList = ({
  handleClickTaskUpdate,
  handleViewThreadClick,
  handleApplyFilter,
  filterChecked,
  recipientDetails,
}) => {
  const workTrackingList = useSelector(
    (state) => state?.arkchatgroup?.workTrackingList
  );
  const threadParents = useSelector(
    (state) => state?.arkchatgroup?.workTrackingThreadParents
  );
  const isLoading = useSelector((state) => state?.localAppReducer?.isLoading);
  const [isScrollingToBottom, setIsScrollingToBottom] = useState(false);
  const [startTaskOpen, setStartTaskOpen] = useState(null);
  const containerRef = useRef(null);

  const handleStartTask = (item) => {
    setStartTaskOpen((prev) =>
      prev?.timetoken === item?.timetoken ? null : item
    );
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    setIsScrollingToBottom(scrollTop + clientHeight >= scrollHeight - 50);
  };

  useEffect(() => {
    if (isScrollingToBottom && workTrackingList?.length > 0) {
      const page = Math.floor(workTrackingList.length / 50) + 1;
      handleApplyFilter(
        filterChecked,
        page,
        workTrackingList[workTrackingList.length - 1]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrollingToBottom]);

  const threadGroups = buildThreadGroups(workTrackingList);

  if (isLoading) return <LoadingSpinner />;
  if (threadGroups.length === 0) {
    return <NoTaskListComponent content="No tasks or approvals found." />;
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        height: "100%",
        padding: "0.4rem",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#B0B0B0",
          borderRadius: "10px",
        },
      }}
    >
      {threadGroups.map((group) => (
        <ThreadGroupCard
          key={group.key}
          group={group}
          threadParents={threadParents}
          handleClickTaskUpdate={handleClickTaskUpdate}
          handleViewThreadClick={handleViewThreadClick}
          startTaskOpen={startTaskOpen}
          setStartTaskOpen={setStartTaskOpen}
          handleStartTask={handleStartTask}
          recipientDetails={recipientDetails}
        />
      ))}
    </Box>
  );
};

export default WorkTrackingList;
